import React, { useEffect, useState } from "react";
import { Header } from "../../../components/SpearParts/Layouts/Header";
import { SearchSection } from "../../../components/Others/Layout/SearchSection";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getCompleteData, getItems, searchPart } from "../Home/OtherHome";
import NoData from "../../../components/NoData/Index";
import BuildingAdvancedSearch from "./BuildingAdvancedSearch";
import { toTitleCase } from "../../../components/utils/StringUtil";
import BldMiddle from "../../../components/Others/Layout/BldMiddle";
import TopNavBar from "../../../components/SpearParts/Layouts/TopNavBar";
import TopSearchBar from "../../../components/SpearParts/Layouts/TopSearchBar";

export default function OtherCategory() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const [bldMaterials, setBldMaterials] = useState([]);
  const [otherCategory, setOtherCategory] = useState([]);
  const [otherBrand, setOtherBrand] = useState([]);
  const [otherModels, setOtherModels] = useState([]);
  const [region, setRegion] = useState([]);
  const [years, setYears] = useState([]);
  const [advResults, setAdvResults] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // Deduplicate array based on _id
  const deduplicate = (arr, key = "_id") => {
    const seen = new Set();
    return arr.filter((item) => {
      const id = item[key] || item?.categoryId?._id || item?._id;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  };

  useEffect(() => {
    if (location.state) {
      setOtherCategory(location.state.otherCategory || []);
      setOtherBrand(location.state.otherBrand || []);
      setOtherModels(location.state.otherModels || []);
      setRegion(location.state.region || []);
      setYears(location.state.years || []);
    } else {
      getCompleteData(setOtherModels, setOtherBrand, setOtherCategory);
      getItems(setRegion);
    }
  }, [location.state]);

  const filters = {
    brandId: searchParams.get("brandId"),
    manufacturerId: searchParams.get("manufacturerId"),
    categoryId: searchParams.get("categoryId"),
    subcategoryId: searchParams.get("subcategoryId"),
  };

  const fetchMaterials = async (page = 1) => {
    if (filters.brandId || filters.manufacturerId || filters.categoryId) {
      try {
        console.log("🔍 Fetching materials with filters:", filters);
        const response = await searchPart(filters, page, itemsPerPage);
        setBldMaterials(response.data || []);
        setTotalPages(response.totalPages);
        setTotalCount(response.totalCount);
        setCurrentPage(response.currentPage);
      } catch (error) {
        console.error("Error fetching materials:", error);
        setBldMaterials([]);
      }
    }
  };

  // ✅ ADD THIS NEW useEffect BELOW fetchMaterials()
  useEffect(() => {
    // Trigger API call whenever brandId / manufacturerId / categoryId changes
    if (filters.brandId || filters.manufacturerId || filters.categoryId) {
      fetchMaterials(1);
    }
  }, [filters.brandId, filters.manufacturerId, filters.categoryId]);

  const getBrandName = (brandId) =>
    otherBrand.find((b) => b._id === brandId)?.name || "Unknown Brand";
  const getModelName = (modelId) =>
    otherModels.find((m) => m._id === modelId)?.name || "Unknown Model";
  const getRegionName = (regionId) =>
    region.find((m) => m._id === regionId)?.name || "Unknown Region";
  const getCategoryName = (category) => category?.name || "Unknown Category";

  const CategoryBoxCard = ({ material }) => {
    const handleCategoryClick = () => {
      if (material.subcategories && material.subcategories.length > 0) {
        // Navigate to Subcategory Page
        navigate(`/others/subcategories?categoryId=${material._id}`, {
          state: {
            category: material,
            otherCategory,
            otherBrand,
            otherModels,
            region,
            years,
          },
        });
      } else {
        // Navigate to Product List
        navigate(`/others/products?categoryId=${material._id}`, {
          state: {
            bldMaterials,
            otherCategory,
            otherBrand,
            otherModels,
            region,
          },
        });
      }
    };

    return (
      <div
        className="category-item"
        onClick={handleCategoryClick}
        style={{ cursor: "pointer" }}
      >
        <div className="d-flex flex-column align-items-center">
          {material?.images?.length > 0 && (
            <img src={material.images[0]} alt={material.name} />
          )}
          <h6>{toTitleCase(material.name)}</h6>
        </div>
      </div>
    );
  };

  const PartCard = ({ material }) => (
    <div className="col-lg-4 col-md-6 col-sm-12 mb-4">
      <Link
        to={`/others/product/single/${material._id}`}
        style={{ textDecoration: "none", color: "inherit" }}
      >
        <div
          className="prod-div p-2 d-flex flex-column align-items-center"
          style={{
            border: "1px solid #ddd",
            borderRadius: "8px",
            height: "100%",
          }}
        >
          {material?.photos?.length > 0 && (
            <img
              src={material.photos[0]?.url}
              alt={material.meterialName}
              style={{
                width: "100%",
                height: "180px",
                objectFit: "contain",
                marginBottom: "15px",
              }}
            />
          )}
          <h6 className="mb-2 text-center">
            {toTitleCase(material.meterialName)}
          </h6>
        </div>
      </Link>
    </div>
  );

  const dedupeByCategory = (arr) => {
    const seen = new Set();
    return arr.filter((item) => {
      const cid = item?.categoryId?._id;
      if (!cid || seen.has(cid)) return false;
      seen.add(cid);
      return true;
    });
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      fetchMaterials(nextPage);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      fetchMaterials(prevPage);
    }
  };

  const sectionStyle = {
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundImage: "url(/images/bnr-bg.png)",
  };

  const beforeImageStyle = {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    width: "360px",
    height: "100%",
    backgroundImage: "url(/images/others.png)",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "left top",
    backgroundSize: "contain",
    pointerEvents: "none",
    zIndex: 1,
  };

  const afterImageStyle = {
    content: '""',
    position: "absolute",
    right: 0,
    // bottom: "600px",
    width: "200px",
    height: "90%",
    backgroundImage: "url(/images/other-after.png)",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right bottom",
    backgroundSize: "contain",
    pointerEvents: "none",
    zIndex: 1,
  };

  return (
    <>
      <div className="New-Rnt-Dashboard" style={sectionStyle}>
        {/* <Header /> */}
        <div className="before-image" style={beforeImageStyle}></div>
        {/* <div className="after-image" style={afterImageStyle}></div> */}
        <div className="Main-div">
          <TopNavBar />
          <div className="TopSearchBar">
            <TopSearchBar />
          </div>
          <div className="container">
            <div className="bnr-div">
              <BldMiddle initialFilters={filters} />
            </div>

            <div className="">
              {/* <div className="col-lg-3">
              <BuildingAdvancedSearch onResults={setAdvResults} />
            </div> */}

              <div className="align-self-start col-lg-12">
                <button
                  className="back btn btn-light"
                  onClick={() => navigate(-1)}
                  style={{
                    border: "none",
                    backgroundColor: "#dbe4f0ff",
                    padding: "8px 14px",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                >
                  <i className="fa fa-long-arrow-left"></i> Back
                </button>

                <div className="table-responsive mt-3">
                  <table className="table product-table">
                    <tbody>
                      <tr>
                        <th style={{ backgroundColor: "#E9EEF4" }}>&nbsp; </th>
                        {/* <th style={{ backgroundColor: "#E9EEF4" }}>Brand</th>
                      <th style={{ backgroundColor: "#E9EEF4" }}>Model</th>
                      <th style={{ backgroundColor: "#E9EEF4" }}>Region</th>
                      <th style={{ backgroundColor: "#E9EEF4" }}>Year</th> */}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="category-grid">
                  {bldMaterials.length > 0 ? (
                    bldMaterials.map((material) => (
                      <CategoryBoxCard key={material._id} material={material} />
                    ))
                  ) : (
                    <NoData />
                  )}
                </div>

                {/* PAGINATION */}
                {bldMaterials.length > 0 && (
                  <div className="pagination-container d-flex justify-content-end align-items-center mt-4">
                    <button
                      className="pagination-btn"
                      disabled={currentPage === 1}
                      onClick={goToPrevPage}
                    >
                      <i className="fa fa-arrow-left"></i>
                    </button>

                    <span className="mx-3">
                      {currentPage} of {totalPages}
                    </span>

                    <button
                      className="pagination-btn"
                      disabled={currentPage === totalPages}
                      onClick={goToNextPage}
                    >
                      <i className="fa fa-arrow-right"></i>
                    </button>
                  </div>
                )}

                {advResults.length > 0 && (
                  <>
                    <h5 className="mt-4 mb-3">Advanced Search Results</h5>
                    <div className="row">
                      {deduplicate(advResults).map((material) => (
                        <PartCard key={material._id} material={material} />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
